import { useSessionStorageState } from "ahooks"
import { HelpEmbed } from "modules/help/Help"
import { Support } from "modules/support/Support"
import { PatientOmnichannel } from "modules/communications/PatientOmnichannel"
import { useFlag } from "modules/flags/flags"
import { type ComponentType, type LazyExoticComponent } from "react"

export type RightSidebarKey = "help" | "support" | "omnichannel"

export interface RightSidebarState {
	key: RightSidebarKey
	/** Optional context data  */
	onBehalfOf?: boolean
	initializeCreate?: boolean
}

export const RIGHT_SIDEBAR_REGISTRY = {
	help: { title: "Help", component: HelpEmbed },
	support: {
		title: "Support tickets",
		component: Support,
	},
	omnichannel: {
		title: "Contact medical team",
		component: PatientOmnichannel,
	},
} satisfies Record<
	RightSidebarKey,
	{ title: string; component: LazyExoticComponent<any> | ComponentType<any> }
>

export const RIGHT_SIDEBAR_SESSION_KEY = "rightSidebar"

/**
 * Manages which right sidebar panel is open, using session storage so that
 * the value persists across in-app navigations but is cleared when the tab
 * is closed.
 *
 * Storing this in session storage instead of the URL avoids the common pitfall
 * where hard navigations (e.g. a "Back" button) silently drop the query param.
 *
 * Returns an object with `state`, `setState`, and `sidebar` where `sidebar`
 * is the registry entry for the current key (or null if none is open / the
 * corresponding feature flag is disabled).
 */
export function useRightSidebar() {
	const [sidebarState, setSidebarState] =
		useSessionStorageState<RightSidebarState | null>(
			RIGHT_SIDEBAR_SESSION_KEY,
			{
				listenStorageChange: true,
				defaultValue: null,
			},
		)
	const { chatMessageInApp, supportRequests, helpDocsEmbed } = useFlag(
		"chatMessageInApp",
		"supportRequests",
		"helpDocsEmbed",
	)

	const key = sidebarState?.key
	let rightSidebar: (typeof RIGHT_SIDEBAR_REGISTRY)[RightSidebarKey] | null =
		null
	if (key) {
		if (key === "omnichannel" && !chatMessageInApp.enabled) {
			rightSidebar = null
		} else if (key === "support" && !supportRequests.enabled) {
			rightSidebar = null
		} else if (key === "help" && !helpDocsEmbed.enabled) {
			rightSidebar = null
		} else {
			rightSidebar = RIGHT_SIDEBAR_REGISTRY[key]
		}
	}

	return { sidebarState, setSidebarState, rightSidebar }
}
